import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel } from "@/components/admin/AdminUI";
import PromptRow from "@/components/admin/PromptRow";
import PromptDialog from "@/components/admin/PromptDialog";
import AddPromptForm from "@/components/admin/AddPromptForm";
import { api } from "@/lib/api";

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export default function Prompts() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/prompts");
  const [order, setOrder] = useState([]);
  const [dragFrom, setDragFrom] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Keep an unsaved local order when only item fields change (toggle/edit); reset when the set of prompts changes.
  useEffect(() => {
    const items = data?.items || [];
    const byId = new Map(items.map((p) => [p.id, p]));
    setOrder((prev) => (prev.length === items.length && prev.every((p) => byId.has(p.id)) ? prev.map((p) => byId.get(p.id)) : items));
  }, [data]);

  const dirty = order.map((p) => p.id).join() !== (data?.items || []).map((p) => p.id).join();
  const replace = (prompt) => {
    setData((d) => ({ items: d.items.map((x) => (x.id === prompt.id ? prompt : x)) }));
  };

  const toggle = async (p, active) => {
    try {
      replace((await api(`/admin/prompts/${p.id}`, { method: "PATCH", body: { active } })).prompt);
      toast.success(active ? "Prompt turned on." : "Prompt turned off.");
    } catch (e) { toast.error(e.message); }
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      setData(await api("/admin/prompts/reorder", { body: { prompt_ids: order.map((p) => p.id) } }));
      toast.success("Order saved.");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const active = order.filter((p) => p.active).length;

  return (
    <div data-testid="admin-prompts-page" className={dirty ? "pb-24" : ""}>
      <PageHeader title="Prompts" description="The questions people answer on their profile. Each person is dealt a random set of active prompts during onboarding." />
      <div className="cw-bg-tint mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="prompts-note">
        <Info size={18} className="cw-brand-deep mt-0.5 shrink-0" aria-hidden />
        <p className="cw-brand-deep">Turned-off prompts stay on profiles that already answered them.</p>
      </div>
      <AddPromptForm onCreated={(prompt) => { setData((d) => ({ items: [...(d?.items || []), prompt] })); toast.success("Prompt added."); }} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="prompts-count">{order.length} prompts · {active} on · drag or use the arrows to reorder</p>
          {order.length ? (
            <ol className="divide-y divide-border" data-testid="prompts-list">
              {order.map((p, i) => (
                <PromptRow key={p.id} p={p} index={i} total={order.length} dragging={dragFrom === i}
                  onMove={(to) => setOrder((o) => move(o, i, to))} onToggle={(v) => toggle(p, v)} onEdit={() => setEditing(p)}
                  onDragStart={() => setDragFrom(i)} onDragEnd={() => setDragFrom(null)}
                  onDragOver={() => { if (dragFrom !== null && dragFrom !== i) { setOrder((o) => move(o, dragFrom, i)); setDragFrom(i); } }} />
              ))}
            </ol>
          ) : <Empty title="No prompts yet" text="Add the first one above." testId="prompts-empty" />}
        </Panel>
      </LoadState>
      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-white/90 backdrop-blur-md lg:left-64" data-testid="prompts-order-bar">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
            <p className="cw-soft text-sm" role="status">The order changed. Save to apply it.</p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setOrder(data.items)} disabled={saving} data-testid="prompts-order-discard-button">Discard</Button>
              <Button onClick={saveOrder} disabled={saving} data-testid="prompts-order-save-button">{saving ? "Saving…" : "Save order"}</Button>
            </div>
          </div>
        </div>
      )}
      <PromptDialog prompt={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={(prompt) => { replace(prompt); toast.success("Prompt updated."); }} />
    </div>
  );
}
